import WebAudioEngine from './WebAudioEngine.js';

/** Supplied Superpowered worklet protocol behind the current application facade. */
export default class SuperpoweredAudioEngine extends WebAudioEngine {
  async createAudioWorklet(channelCount, offline = false) {
    await this.destroyAudioWorklet();
    const context = this.webaudioManager.audioContext;
    const base = process.env.PUBLIC_URL || '';
    if (!context.audioWorklet) throw new Error('Superpowered requires AudioWorklet support (HTTPS or localhost).');
    await context.audioWorklet.addModule(`${base}/processorScripts/superpowered-mixer-runtime-v1.js`);
    const response = await fetch(`${base}/lib/superpowered/superpowered-runtime-v1.wasm`);
    if (!response.ok) throw new Error(`Could not load Superpowered audio (${response.status}).`);
    const moduleBytes = await response.arrayBuffer();
    const node = new AudioWorkletNode(context, 'superpowered-mixer-runtime-v1', {
      numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [2],
      channelCount: 2, channelCountMode: 'explicit',
      processorOptions: { moduleBytes, samplerate: context.sampleRate, channelCount }
    });
    this.processorNode = node;
    node.sendMessageToAudioScope = message => node.port.postMessage(message);
    const ready = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Superpowered initialization timed out. Choose Web Audio in Settings to recover.')), 30000);
      this.spReady = { resolve: () => { clearTimeout(timeout); this.spReady = null; resolve(); },
        reject: error => { clearTimeout(timeout); this.spReady = null; reject(error); } };
    });
    node.port.onmessage = ({ data }) => {
      if (this.processorNode !== node) return;
      if (data.event === 'worklet-log') return;
      if (data.event === 'ready' && this.spReady) this.spReady.resolve();
      if (data.event === 'error') this.rejectSuperpowered(new Error(data.message));
      if (this.spPending && data.event === this.spPending.event) this.spPending.resolve();
      this.onMessageProcessorAudioScope(data);
    };
    node.onprocessorerror = () => this.rejectSuperpowered(new Error('The Superpowered audio processor failed.'));
    this.stereoInputNode.connect(node); node.connect(context.destination);
    try {
      if (!offline) await context.resume();
      await ready;
    } catch (error) { await this.destroyAudioWorklet(); throw error; }
  }

  rejectSuperpowered(error) {
    if (this.spReady) this.spReady.reject(error);
    if (this.spPending) this.spPending.reject(error);
    this.playing = false;
  }

  async destroyAudioWorklet() {
    clearInterval(this.stateInterval); this.stateInterval = null;
    this.rejectSuperpowered(new Error('Audio loading was cancelled'));
    const node = this.processorNode; this.processorNode = null;
    if (node) {
      try { this.stereoInputNode.disconnect(node); } catch (_) {}
      node.port.onmessage = null; node.onprocessorerror = null;
      node.port.postMessage({ type: 'destruct' }); node.disconnect(); node.port.close();
    }
    this.playing = false;
  }

  waitForSuperpowered(event, payload) {
    const node = this.processorNode;
    const previous = this.spLoadQueue || Promise.resolve();
    const operation = previous.catch(() => {}).then(() => {
      if (this.processorNode !== node) throw new Error('Audio loading was cancelled');
      return this.performSuperpoweredLoad(event, payload);
    });
    this.spLoadQueue = operation;
    return operation;
  }

  performSuperpoweredLoad(event, payload) {
    const node = this.processorNode;
    if (!node) return Promise.reject(new Error('Audio engine is not initialized'));
    if (this.spPending) return Promise.reject(new Error('An audio load is already in progress'));
    return new Promise((resolve, reject) => {
      const finish = (callback, value) => { clearTimeout(timeout); this.spPending = null; callback(value); };
      const timeout = setTimeout(() => finish(reject, new Error('Superpowered audio loading timed out')), 60000);
      this.spPending = { event, resolve: () => finish(resolve), reject: error => finish(reject, error) };
      node.sendMessageToAudioScope({ type: 'command', payload });
    });
  }

  async loadChannelEncodedBuffer(encodedBuffer, index) {
    const bytes = encodedBuffer instanceof ArrayBuffer ? encodedBuffer.slice(0)
      : ArrayBuffer.isView(encodedBuffer) ? encodedBuffer.buffer.slice(encodedBuffer.byteOffset, encodedBuffer.byteOffset + encodedBuffer.byteLength) : null;
    if (!bytes) throw new Error('Missing encoded audio data');
    await this.waitForSuperpowered('singleChannelBufferLoaded', { id: 'loadChannelEncodedBuffer', encodedBuffer: bytes, index });
  }

  async loadChannelEncodedBuffers(buffers) {
    await this.waitForSuperpowered('channelBuffersLoaded', { id: 'loadChannelEncodedBuffers', encodedBuffers: buffers });
    this.startCurrentEngineStatePolling();
  }

  async loadChannelBuffers(buffers) {
    await this.waitForSuperpowered('channelBuffersLoaded', { id: 'loadChannelBuffers', buffers });
    this.startCurrentEngineStatePolling();
  }

  destructProcessorMixerEngine(newChannelCount) {
    if (this.processorNode) this.processorNode.sendMessageToAudioScope({ type: 'destruct-mixer-engine', payload: { newChannelCount } });
  }

  async sendPlayCommand(offlineContext = false) {
    if (!this.processorNode) return;
    if (!offlineContext) await this.webaudioManager.audioContext.resume();
    this.processorNode.sendMessageToAudioScope({ type: 'command', payload: { id: 'startPlayback' } });
    this.playing = true;
  }

  sendRecordCommand() {
    const node = this.processorNode;
    if (!node || this.recording) return;
    this.recording = true;
    this.webaudioManager.audioContext.resume().then(() => {
      if (this.recording && this.processorNode === node) node.sendMessageToAudioScope({ type: 'command', payload: { id: 'startRecording' } });
    }).catch(error => {
      this.recording = false;
      this.onMessageProcessorAudioScope({ event: 'error', message: error.message });
    });
  }

  async requestUserMicrophone() {
    if (!this.userGrantedInput) {
      this.userInputStream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false }, video: false });
      this.userInputStreamSourceNode = this.webaudioManager.audioContext.createMediaStreamSource(this.userInputStream);
      this.userInputStreamSourceNode.connect(this.stereoInputNode); this.userGrantedInput = true;
    }
    return this.userGrantedInput;
  }

  async sendGlobalParamChange(paramName, value) {
    if (this.processorNode) this.processorNode.sendMessageToAudioScope({ type: 'parameterChange', data: { scope: 'global', paramName, channelIndex: null, value } });
  }
}
